import { FC, Fragment, useEffect, useId, useState } from 'react'
import type { MouseEvent } from 'react'
import MoveInDate from '@/components/icons/map/filter/moveInDate'
import { Box, Popover, Typography } from '@mui/material'
import moment from 'moment'
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { statsigLog } from '@/lib/statsigAndGa'
import CloseCircle from '@/components/icons/map/filter/closeCircle'
import { PRESET_MOVE_IN_DATE_LIST, MOVE_IN_DATE_DEFAULT_LABEL } from './filterConfig'

interface MoveInDateFilterProps {
  onApply: (budget: number | null) => void
  moveInDate: number | null
}
const calcLabel = (timestamp: number | null) => {
  if (!timestamp) {
    return 'Move-in Date'
  }
  return moment(timestamp).format('YYYY-MM-DD')
}

const MoveInDateFilter: FC<MoveInDateFilterProps> = props => {
  const { onApply, moveInDate } = props
  const [open, setOpen] = useState(false)
  const popoverId = useId()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [selected, setSelected] = useState<number | null>(moveInDate)

  const [label, setLabel] = useState(MOVE_IN_DATE_DEFAULT_LABEL)

  const onClickButton = (event: MouseEvent<HTMLButtonElement>) => {
    statsigLog('move_in_date_filter_click')
    setAnchorEl(event.currentTarget)
    setOpen(true)
  }
  const onSelectedPresetMoveInDate = (index: number) => {
    let select: number | null = null
    switch (index) {
      case 0:
        select = moment.utc().valueOf()
        break
      case 1:
        select = moment.utc().add(1, 'months').valueOf()
        break
      case 2:
        select = moment.utc().add(3, 'months').valueOf()
        break
      case -1:
      default:
        break
    }
    setSelected(select)
  }
  const onClickClear = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    const nullTimestamp = null
    setLabel(calcLabel(nullTimestamp))
    onApply(nullTimestamp)
  }

  const onPopoverReset = () => {
    setSelected(moveInDate)
  }

  const onPopoverApply = () => {
    setOpen(false)
    setLabel(calcLabel(selected))
    onApply(selected)
  }

  useEffect(() => {
    setLabel(calcLabel(moveInDate))
    setSelected(moveInDate)
  }, [moveInDate])

  return (
    <>
      <Box
        aria-describedby={popoverId}
        component="button"
        onClick={onClickButton}
        className={`flex items-center gap-2 py-[6px] px-4 border  rounded-20 hover:bg-gray-100 ${
          label !== MOVE_IN_DATE_DEFAULT_LABEL ? 'border-primary bg-gray-100' : 'border-gray-200'
        }`}
      >
        <MoveInDate className="w-4 h-4" />
        <Typography className="text-primary text-sm">{label}</Typography>
        {label !== MOVE_IN_DATE_DEFAULT_LABEL && (
          <CloseCircle className="w-4 h-4" onClick={onClickClear} />
        )}
      </Box>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={() => {
          setOpen(false)
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        classes={{
          paper: 'rounded-xl bg-white p-4 mt-4 shadow-[0_1px_7px_0_#242A2E1A]'
        }}
      >
        <Box component="div" className="flex flex-wrap items-center gap-3 w-80">
          {PRESET_MOVE_IN_DATE_LIST.map(m => (
            <Box
              key={m.label}
              component="button"
              onClick={() => onSelectedPresetMoveInDate(m.value)}
              className={`text-sm text-primary px-4 py-2 border rounded-20 ${'border-gray-200'}`}
            >
              {m.label}
            </Box>
          ))}
          <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>
            <StaticDatePicker
              onChange={date => {
                setSelected(moment.utc(date).valueOf())
              }}
              timezone="system"
              value={moment.utc(selected)}
              disablePast
              className="font-averta font-bold"
              displayStaticWrapperAs="desktop"
            />
          </LocalizationProvider>
        </Box>
        <Box component="div" className="mt-4 flex justify-between items-end">
          <Typography
            onClick={onPopoverReset}
            className="text-primary font-semibold underline cursor-pointer"
          >
            Reset
          </Typography>
          <Box
            component="button"
            onClick={onPopoverApply}
            className="bg-brand text-white font-semibold rounded-xl py-2 px-6"
          >
            Apply
          </Box>
        </Box>
      </Popover>
    </>
  )
}

export default MoveInDateFilter
